import { Skeleton, TextField } from '@mui/material';
import { FC, useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import FieldEditComponent from 'components/Atoms/Form/FieldEditComponent/FieldEditComponent';
import { UpdateCompanyMutationFn, UpdateMyCompanyMutationFn } from 'src/graphql/generated/hooks';
import { UpdateMyCompanyInput } from 'src/graphql/generated/schema';

import { AdminCompanyDetailsQuery, MyCompanyDetailsQuery } from '../../graphql/generated/operations';

type FormInput = Pick<UpdateMyCompanyInput, 'name'>;

interface EditCompanyNameProps {
  data: MyCompanyDetailsQuery['me'] | AdminCompanyDetailsQuery | undefined;
  loading: boolean;
  mutation: UpdateMyCompanyMutationFn | UpdateCompanyMutationFn;
  companyId?: string;
}

const EditCompanyName: FC<EditCompanyNameProps> = ({ data, loading, mutation, companyId }) => {
  const { t } = useTranslation('translation');

  const { getValues, control, reset } = useForm<FormInput>({
    values: { name: data?.company.name ?? '' },
  });

  const onSubmit = useCallback(async () => {
    if (!getValues('name')) {
      reset();
      return toast.error(t('messages.error.invalid-company-name'));
    }
    mutation({
      variables: { input: { companyId, name: getValues('name') } },
      update(_, { data }) {
        if (data) {
          toast.success(t('messages.success.company-name-updated'));
        }
      },
    });
  }, [getValues, mutation, companyId, reset, t]);

  if (loading || !data) {
    return (
      <>
        <Skeleton />
        <Skeleton />
      </>
    );
  }

  return (
    <FieldEditComponent
      title={t('pages.company-details-page.name')}
      value={getValues('name')}
      onCancel={reset}
      onSave={onSubmit}
    >
      <Controller
        control={control}
        defaultValue={data.company.name}
        name="name"
        render={({ field }) => <TextField {...field} fullWidth inputProps={{ 'data-testid': 'input-company-name' }} />}
        rules={{ required: true }}
      />
    </FieldEditComponent>
  );
};

export default EditCompanyName;
